// useTableCRUD.ts
import { useState, useEffect } from "react"; // <-- Importa useEffect
import { tableService } from "./api";
import { sortByProperty } from "./utils";

export const useTableCRUD = <T extends { id: number; [key: string]: any }>(
  initialData: T[]
) => {
  // Esto inicializa el estado la primera vez, lo cual está bien.
  const [data, setData] = useState<T[]>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setData(initialData ?? []);
  }, [initialData]);

  const createItem = async (url: string, itemData: any, keyProp: string, sortProp: string) => {
    setIsLoading(true);
    try {
      const response = await tableService.create(url, itemData);
      let newItem: T;

      if (response.data && response.data.item) {
        newItem = response.data.item;
      } else if (response.data && response.data.newId) {
        newItem = { ...itemData, [keyProp]: response.data.newId };
      } else {
        newItem = response.data;
      }

      if (newItem) {
        setData((prev) => {
          const next = sortByProperty(
            [...prev, newItem as T],
            sortProp as keyof T
          );
          return next;
        });
      }
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateItem = async (
    url: string,
    id: number,
    itemData: any,
    keyProp: string, 
    sortProp: string
  ) => {
    setIsLoading(true);
    try {
      const response = await tableService.update(url, id, itemData);
      setData((prev) =>
        prev.map((item) => (item[keyProp] === id ? response.data : item))
      );
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteItem = async (url: string, id: number, keyProp: string, sortProp: string) => {
    setIsLoading(true);
    try {
      await tableService.delete(url, id);
      setData((prev) => prev.filter((item) => item[keyProp] !== id));
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    data,
    setData,
    isLoading,
    error,
    createItem,
    updateItem,
    deleteItem,
  };
};
